/**
 * MatterUpdatesPage - Paginated list of matters with pending entity changes
 *
 * Uses generic DataTable with server-side pagination, sorting, and filtering.
 * Mirrors the CrossMatterChangesListPage pattern with matter-aggregated rows.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { DataTable } from '@/components/common/DataTable';
import type { TableColumn } from '@/components/common/DataTable/types';
import {
  ACTIVE_MATTER_STATUSES,
  getStatusBgColor,
} from '@/components/matters/config/matterStatusConfig';
import type { BadgeColor } from '@/components/ui/catalyst/badge';
import { Badge } from '@/components/ui/catalyst/badge';
import type { PaginationState } from '@/hooks/usePagination';
import { usePagination } from '@/hooks/usePagination';
import type {
  EntityCategory,
  EntityChangesListApiArg,
  EntityChangeSummarySchema,
} from '@/services/api/generated/entityChangesService';
import {
  useEntityChangesFilterOptionsQuery,
  useEntityChangesListQuery,
} from '@/services/api/generated/entityChangesService';
import type { PaginatedResponse } from '@/services/types/pagination-types';
import { getResults, getTotalCount } from '@/utils/pagination';

/* ------------------------------------------------------------------ */
/*  Category display config                                           */
/* ------------------------------------------------------------------ */

const CATEGORY_DISPLAY: Record<
  EntityCategory,
  { label: string; color: BadgeColor }
> = {
  PROVIDER: { label: 'Providers', color: 'purple' },
  TREATMENT_REQUEST: { label: 'TRRs', color: 'blue' },
  COVERAGE: { label: 'Coverages', color: 'amber' },
  TASK: { label: 'Tasks', color: 'green' },
  TASK_UPDATE: { label: 'Task Updates', color: 'cyan' },
  STAFF_NOTE: { label: 'Notes', color: 'pink' },
  DOCUMENT: { label: 'Documents', color: 'sky' },
  CASE_EXPENSE: { label: 'Case Expenses', color: 'orange' },
  OTHER: { label: 'Other', color: 'zinc' },
};

/* ------------------------------------------------------------------ */
/*  Filter key mapping (column id → backend key)                      */
/* ------------------------------------------------------------------ */

const FILTER_KEY_MAP: Record<string, string> = {
  firm_name: 'firm',
  matter_name: 'matter_id',
  matter_status: 'matter_status',
  case_manager_name: 'case_manager',
};

function getBackendFilterKey(columnId: string): string {
  return FILTER_KEY_MAP[columnId] ?? columnId;
}

function getColumnIdFromBackend(backendKey: string): string {
  const entry = Object.entries(FILTER_KEY_MAP).find(
    ([, v]) => v === backendKey,
  );
  return entry ? entry[0] : backendKey;
}

/* ------------------------------------------------------------------ */
/*  Sort key mapping (column id → backend ordering field)             */
/* ------------------------------------------------------------------ */

const SORT_KEY_MAP: Record<string, string> = {
  matter_name: 'name',
  firm_name: 'firm__name',
};

function getOrderingField(columnId: string): string {
  return SORT_KEY_MAP[columnId] ?? columnId;
}

/* ------------------------------------------------------------------ */
/*  Query param builder                                                */
/* ------------------------------------------------------------------ */

function toArray(
  value: string | string[] | boolean | number | null | undefined,
): string[] | undefined {
  if (value === undefined || value === null) return undefined;
  if (Array.isArray(value)) return value.length > 0 ? value : undefined;
  return [String(value)];
}

function buildQueryParams(state: PaginationState): EntityChangesListApiArg {
  const f = state.filters;
  return {
    limit: state.limit,
    offset: state.offset,
    ordering: state.ordering || undefined,
    firm: toArray(f.firm),
    matterId: toArray(f.matter_id),
    matterName: (f.matter_name as string) || undefined,
    matterStatus: toArray(f.matter_status),
    caseManager: toArray(f.case_manager),
  };
}

/* ------------------------------------------------------------------ */
/*  Relative date formatter                                            */
/* ------------------------------------------------------------------ */

function formatRelativeDate(dateString: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

/* ------------------------------------------------------------------ */
/*  Cell renderers                                                     */
/* ------------------------------------------------------------------ */

function renderMatterCell(row: EntityChangeSummarySchema) {
  return (
    <a
      href={`/matter-updates/${row.matter_id}`}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey) {
          // Let the browser handle cmd/ctrl+click (new tab) but stop the row onClick
          e.stopPropagation();
        } else {
          // Plain click — prevent <a> navigation, let row onRowClick handle SPA nav
          e.preventDefault();
        }
      }}
      className="font-medium text-gray-900 dark:text-white"
    >
      {row.matter_name}
    </a>
  );
}

function renderPendingCell(row: EntityChangeSummarySchema) {
  return (
    <Badge color="red" className="text-xs">
      {row.total_changes}
    </Badge>
  );
}

function renderCategoriesCell(row: EntityChangeSummarySchema) {
  return (
    <div className="flex flex-wrap gap-1">
      {row.category_counts
        .filter((c) => c.count > 0)
        .map((c) => {
          const display = CATEGORY_DISPLAY[c.category] ?? {
            label: c.category,
            color: 'zinc' as BadgeColor,
          };
          return (
            <Badge
              key={c.category}
              color={display.color}
              className="text-xs"
            >
              {display.label} {c.count}
            </Badge>
          );
        })}
    </div>
  );
}

function renderEarliestChangeCell(row: EntityChangeSummarySchema) {
  return (
    <span className="text-sm text-gray-500">
      {formatRelativeDate(row.oldest_pending_change ?? null)}
    </span>
  );
}

function renderCaseManagerCell(row: EntityChangeSummarySchema) {
  return (
    <span className="text-sm text-gray-700 dark:text-gray-300">
      {row.case_manager_name ?? '-'}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Column factory                                                     */
/* ------------------------------------------------------------------ */

function renderStatusCell(row: EntityChangeSummarySchema) {
  const bgColor = getStatusBgColor(row.matter_status);
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${bgColor}`}
    >
      {row.matter_status}
    </span>
  );
}

interface CreateColumnsOptions {
  matterOptions: { label: string; value: string }[];
  firmOptions: { label: string; value: string }[];
  statusOptions: { label: string; value: string }[];
  caseManagerOptions: { label: string; value: string }[];
}

function createMatterUpdateColumns({
  matterOptions,
  firmOptions,
  statusOptions,
  caseManagerOptions,
}: CreateColumnsOptions): TableColumn<EntityChangeSummarySchema>[] {
  return [
    {
      id: 'matter_name',
      header: 'Matter',
      accessorFn: (row) => row.matter_name,
      cell: renderMatterCell,
      sortable: true,
      sort: {
        sortKey: 'name',
        labels: { asc: 'A to Z', desc: 'Z to A' },
      },
      filter: {
        type: 'multiselect',
        options: matterOptions,
        searchable: true,
        placeholder: 'Filter by matter',
      },
      width: '280px',
      truncate: false,
    },
    {
      id: 'matter_status',
      header: 'Status',
      accessorFn: (row) => row.matter_status,
      cell: renderStatusCell,
      filter: {
        type: 'multiselect',
        options: statusOptions,
        searchable: true,
        placeholder: 'Filter by status',
      },
      width: '160px',
      truncate: false,
    },
    {
      id: 'firm_name',
      header: 'Firm',
      accessorFn: (row) => row.firm_name,
      sortable: true,
      sort: {
        sortKey: 'firm__name',
        labels: { asc: 'A to Z', desc: 'Z to A' },
      },
      filter: {
        type: 'multiselect',
        options: firmOptions,
        searchable: true,
        placeholder: 'Filter by firm',
      },
      width: '200px',
    },
    {
      id: 'total_changes',
      header: 'Pending',
      accessorFn: (row) => row.total_changes,
      cell: renderPendingCell,
      sortable: true,
      sort: {
        labels: { asc: 'Fewest first', desc: 'Most first' },
      },
      width: '100px',
      align: 'center',
    },
    {
      id: 'categories',
      header: 'Categories',
      accessorFn: (row) =>
        row.category_counts
          .filter((c) => c.count > 0)
          .map((c) => c.category)
          .join(', '),
      cell: renderCategoriesCell,
      truncate: false,
    },
    {
      id: 'oldest_pending_change',
      header: 'Oldest Change',
      accessorFn: (row) => row.oldest_pending_change ?? null,
      cell: renderEarliestChangeCell,
      sortable: true,
      sort: {
        labels: { asc: 'Newest first', desc: 'Oldest first' },
      },
      width: '140px',
    },
    {
      id: 'case_manager_name',
      header: 'Case Manager',
      accessorFn: (row) => row.case_manager_name ?? '',
      cell: renderCaseManagerCell,
      filter: {
        type: 'multiselect',
        options: caseManagerOptions,
        searchable: true,
        placeholder: 'Filter by case manager',
      },
      width: '160px',
    },
  ];
}

/* ------------------------------------------------------------------ */
/*  Page component                                                     */
/* ------------------------------------------------------------------ */

export const MatterUpdatesPage: React.FC = () => {
  const navigate = useNavigate();

  // Pagination / sorting / filtering state (URL-synced)
  const defaultActiveStatuses = ACTIVE_MATTER_STATUSES as string[];

  const { state, actions, searchInput, setSearchInput } = usePagination({
    defaultLimit: 50,
    defaultSort: '-oldest_pending_change',
    defaultFilters: { matter_status: defaultActiveStatuses },
  });

  // Build query params
  const queryParams = useMemo(() => {
    const base = buildQueryParams(state);
    if (searchInput) {
      return { ...base, matterName: searchInput };
    }
    return base;
  }, [state, searchInput]);

  // Data fetching
  const { data: rawData, isLoading } = useEntityChangesListQuery(queryParams);
  const data = rawData as
    | PaginatedResponse<EntityChangeSummarySchema>
    | undefined;
  const matters = useMemo(() => getResults(data), [data]);
  const totalCount = getTotalCount(data);

  // Filter options (distinct matters + firms with pending entity changes)
  const { data: filterOptionsData } = useEntityChangesFilterOptionsQuery();

  const matterOptions = useMemo(
    () =>
      (filterOptionsData?.matters ?? []).map((m) => ({
        label: m.name,
        value: m.id,
      })),
    [filterOptionsData?.matters],
  );

  const firmOptions = useMemo(
    () =>
      (filterOptionsData?.firms ?? []).map((f) => ({
        label: f.name,
        value: f.id,
      })),
    [filterOptionsData?.firms],
  );

  const statusOptions = useMemo(
    () =>
      (filterOptionsData?.matter_statuses ?? []).map((s) => ({
        label: s.name,
        value: s.id,
      })),
    [filterOptionsData?.matter_statuses],
  );

  const caseManagerOptions = useMemo(
    () =>
      (filterOptionsData?.case_managers ?? []).map((cm) => ({
        label: cm.name,
        value: cm.id,
      })),
    [filterOptionsData?.case_managers],
  );

  // Column definitions
  const columns = useMemo(
    () =>
      createMatterUpdateColumns({
        matterOptions,
        firmOptions,
        statusOptions,
        caseManagerOptions,
      }),
    [matterOptions, firmOptions, statusOptions, caseManagerOptions],
  );

  // Handlers
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
  };

  const handleFilterChange = (
    columnId: string,
    value: string | string[] | null,
  ) => {
    const backendKey = getBackendFilterKey(columnId);
    const newFilters = { ...state.filters };

    if (value === null || (Array.isArray(value) && value.length === 0)) {
      delete newFilters[backendKey];
    } else {
      newFilters[backendKey] = value;
    }

    actions.setFilters(newFilters);
  };

  const handleSortChange = (columnId: string, direction?: 'asc' | 'desc') => {
    const orderingField = getOrderingField(columnId);
    actions.setOrdering(orderingField, direction);
  };

  const currentSortField = useMemo(
    () => state.ordering.replace(/^-/, ''),
    [state.ordering],
  );

  const filterValues = useMemo(() => {
    const result: Record<string, string | string[] | null> = {};
    Object.entries(state.filters).forEach(([backendKey, value]) => {
      const columnId = getColumnIdFromBackend(backendKey);
      if (typeof value === 'boolean' || typeof value === 'number') {
        result[columnId] = String(value);
      } else {
        result[columnId] = value;
      }
    });
    return result;
  }, [state.filters]);

  return (
    <div className="flex h-[calc(100vh-64px)] flex-col gap-2 overflow-hidden bg-gray-50 px-10 py-2 dark:bg-zinc-950">
      <div className="flex flex-shrink-0 items-center justify-between">
        <h1 className="font-serif text-4xl font-medium text-black dark:text-white">
          Matter Updates
        </h1>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-300 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
        <DataTable
          data={matters}
          columns={columns}
          getRowKey={(row) => row.matter_id}
          searchEnabled
          searchPlaceholder="Search by matter name"
          searchValue={searchInput}
          onSearchChange={handleSearchChange}
          disableClientFilter
          paginationEnabled
          currentPage={actions.getCurrentPage()}
          totalPages={actions.getTotalPages(totalCount)}
          totalCount={totalCount}
          pageSize={state.limit}
          onPageChange={actions.setPage}
          onPageSizeChange={actions.setLimit}
          onSortChange={handleSortChange}
          sortField={currentSortField}
          sortDirection={state.ordering.startsWith('-') ? 'desc' : 'asc'}
          onFilterChange={handleFilterChange}
          filterValues={filterValues}
          isLoading={isLoading}
          emptyMessage="No matters with pending changes"
          onRowClick={(row) => navigate(`/matter-updates/${row.matter_id}`)}
        />
      </div>
    </div>
  );
};
